<template>
  <div class="online-sale-notification-emails mb-4">
    <Card cardClasses="shadow-sm border-0">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h4 class="m-0 text-primary">
          <i class="bi bi-envelope me-2"></i>Online Sale Notification Emails
        </h4>
        <button class="btn btn-primary btn-sm" @click="openAddModal">
          <i class="bi bi-plus-lg me-1"></i> Add Email
        </button>
      </div>

      <p class="text-muted small mb-3">
        Configure email addresses that should receive notifications when online sales occur for this store.
      </p>

      <!-- Loading State -->
      <div v-if="loading" class="text-center p-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Email List -->
      <div v-else-if="emails.length > 0" class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>Email Address</th>
              <th>Status</th>
              <th>Added</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="email in emails" :key="email.id">
              <td>
                <i class="bi bi-envelope me-2 text-primary"></i>
                {{ email.email }}
              </td>
              <td>
                <span v-if="email.active" class="badge bg-success">Active</span>
                <span v-else class="badge bg-secondary">Inactive</span>
              </td>
              <td class="small text-muted">
                {{ formatDate(email.created_at) }}
              </td>
              <td class="text-end">
                <button class="btn btn-sm btn-outline-secondary me-1" @click="toggleActive(email)" title="Toggle status">
                  <i :class="email.active ? 'bi bi-toggle-on' : 'bi bi-toggle-off'"></i>
                </button>
                <button class="btn btn-sm btn-outline-primary me-1" @click="editEmail(email)" title="Edit">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" @click="confirmDelete(email)" title="Delete">
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center p-5 text-muted">
        <i class="bi bi-inbox display-1 d-block mb-3 opacity-25"></i>
        <p>No notification emails configured yet.</p>
        <button class="btn btn-primary btn-sm" @click="openAddModal">
          <i class="bi bi-plus-lg me-1"></i> Add First Email
        </button>
      </div>
    </Card>

    <!-- Add/Edit Modal -->
    <Modal :open="showModal" @closed="closeModal">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi me-2 text-primary" :class="isEditing ? 'bi-pencil-square' : 'bi-plus-lg'"></i>
          {{ isEditing ? 'Edit Email' : 'Add Notification Email' }}
        </h5>
        <button @click="closeModal" type="button" class="btn-close outline-none" aria-label="Close"></button>
      </div>
      <form @submit.prevent="saveEmail">
        <div class="modal-body">
          <div class="mb-3">
            <label for="emailInput" class="form-label">Email Address <span class="text-danger">*</span></label>
            <input 
              id="emailInput"
              v-model="formData.email" 
              type="email" 
              class="form-control"
              :class="{ 'is-invalid': errors.email }"
              placeholder="name@example.com"
              required
            />
            <div v-if="errors.email" class="invalid-feedback d-block">
              {{ errors.email[0] }}
            </div>
          </div>

          <div class="mb-3">
            <div class="form-check">
              <input 
                id="activeCheck"
                v-model="formData.active" 
                type="checkbox" 
                class="form-check-input"
              />
              <label for="activeCheck" class="form-check-label">
                Active - Receive notifications
              </label>
            </div>
          </div>
        </div>
        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-outline-secondary" @click="closeModal">Cancel</button>
          <button type="submit" class="btn btn-primary" :disabled="saving">
            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
            {{ saving ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- Delete Confirmation Modal -->
    <Modal :open="showDeleteConfirm" @closed="showDeleteConfirm = false">
      <div class="modal-header bg-danger-light">
        <h5 class="modal-title m-0 text-danger">
          <i class="bi bi-exclamation-triangle me-2"></i> Delete Email
        </h5>
        <button @click="showDeleteConfirm = false" type="button" class="btn-close outline-none" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to delete <strong>{{ emailToDelete?.email }}</strong>?</p>
        <p class="text-muted small mb-0">This action cannot be undone.</p>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="showDeleteConfirm = false">Cancel</button>
        <button type="button" class="btn btn-danger" @click="deleteEmail" :disabled="deleting">
          <span v-if="deleting" class="spinner-border spinner-border-sm me-2"></span>
          {{ deleting ? 'Deleting...' : 'Delete' }}
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import Card from '@/components/card/Card.vue';
import Modal from '@/components/modal/Modal.vue';
import notify from '@/utils/notify';
import axios from 'axios';

export default {
  name: 'OnlineSaleNotificationEmails',
  components: {
    Card,
    Modal,
  },
  props: {
    storeId: {
      type: [String, Number],
      required: false,
      default: '',
    },
  },
  data() {
    return {
      emails: [],
      loading: false,
      showModal: false,
      showDeleteConfirm: false,
      isEditing: false,
      saving: false,
      deleting: false,
      emailToDelete: null,
      formData: {
        email: '',
        active: true,
      },
      errors: {},
    };
  },
  watch: {
    storeId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadEmails();
        }
      },
    },
  },
  methods: {
    getApiUrl(path) {
      const baseUrl = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '');
      const cleanPath = path.replace(/^\/+/, '');
      return `${baseUrl}/${cleanPath}`;
    },

    getHeaders() {
      const token = sessionStorage.getItem('access_token');
      return {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      };
    },

    async loadEmails() {
      if (!this.storeId) return;
      this.loading = true;
      try {
        const response = await axios.get(
          this.getApiUrl(`stores/${this.storeId}/online-sale-emails`),
          { headers: this.getHeaders() }
        );
        this.emails = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      } catch (error) {
        console.error('Error loading emails:', error);
        notify.alert.error('Failed to load notification emails');
      } finally {
        this.loading = false;
      }
    },

    openAddModal() {
      this.isEditing = false;
      this.formData = { email: '', active: true };
      this.errors = {};
      this.showModal = true;
    },

    editEmail(email) {
      this.isEditing = true;
      this.formData = { ...email };
      this.errors = {};
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.formData = { email: '', active: true };
      this.errors = {};
    },

    async saveEmail() {
      this.saving = true;
      this.errors = {};
      try {
        if (this.isEditing) {
          await axios.put(
            this.getApiUrl(`stores/online-sale-emails/${this.formData.id}`),
            this.formData,
            { headers: this.getHeaders() }
          );
          notify.alert.success('Email updated successfully');
        } else {
          await axios.post(
            this.getApiUrl('stores/online-sale-emails'),
            {
              store_id: this.storeId,
              ...this.formData,
            },
            { headers: this.getHeaders() }
          );
          notify.alert.success('Email added successfully');
        }
        this.closeModal();
        await this.loadEmails();
      } catch (error) {
        console.error('Error saving email:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          notify.alert.error('Failed to save email');
        }
      } finally {
        this.saving = false;
      }
    },

    confirmDelete(email) {
      this.emailToDelete = email;
      this.showDeleteConfirm = true;
    },

    async deleteEmail() {
      this.deleting = true;
      try {
        await axios.delete(
          this.getApiUrl(`stores/online-sale-emails/${this.emailToDelete.id}`),
          { headers: this.getHeaders() }
        );
        notify.alert.success('Email deleted successfully');
        this.showDeleteConfirm = false;
        await this.loadEmails();
      } catch (error) {
        console.error('Error deleting email:', error);
        notify.alert.error('Failed to delete email');
      } finally {
        this.deleting = false;
      }
    },

    async toggleActive(email) {
      try {
        await axios.patch(
          this.getApiUrl(`stores/online-sale-emails/${email.id}/toggle`),
          {},
          { headers: this.getHeaders() }
        );
        email.active = !email.active;
        notify.alert.success(email.active ? 'Email activated' : 'Email deactivated');
      } catch (error) {
        console.error('Error toggling email:', error);
        notify.alert.error('Failed to update email status');
      }
    },

    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.bg-danger-light {
  background-color: rgba(220, 53, 69, 0.1);
}

.opacity-25 {
  opacity: 0.25;
}
</style>
